import { Component, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Dialog } from '../../components/ui/Dialog';
import { selectUserToken } from '../../redux/features/user/userSelectors';

import { ScopeStep } from './steps/ScopeStep';
import { PreviewStep } from './steps/PreviewStep';
import { ProgressStep } from './steps/ProgressStep';
import { ResultsStep } from './steps/ResultsStep';
import { BackgroundJobPill } from './components/BackgroundJobPill';
import {
  useImportFlowState,
  STEPS,
} from './hooks/useImportFlowState';
import {
  useGetBatchStatusQuery,
  TERMINAL_BATCH_STATUSES,
} from './api/parserImportApi';

// Simple error boundary — one per step so a broken chart doesn't crash the
// whole modal.
class StepBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ImportFlow] step crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center gap-3 py-10 w-full">
          <span className="text-sm text-redColor">
            Something went wrong: {String(this.state.error?.message || this.state.error)}
          </span>
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded border border-gray-200 hover:bg-lightGrayColor"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const STEP_TITLES = {
  [STEPS.scope]: 'Choose scope',
  [STEPS.preview]: 'Review records',
  [STEPS.progress]: 'Importing…',
  [STEPS.results]: 'Import complete',
};

const StepDots = ({ steps, currentIndex }) => (
  <div data-testid="parser-import-stepper" className="flex items-center gap-2">
    {steps.map((s, i) => {
      const isActive = i === currentIndex;
      const isDone = i < currentIndex;
      return (
        <span
          key={s}
          className={
            'h-2 rounded-full transition-all ' +
            (isActive
              ? 'w-8 bg-purpleColor'
              : isDone
              ? 'w-2 bg-greenColor'
              : 'w-2 bg-gray-200')
          }
          aria-label={`Step ${i + 1}: ${STEP_TITLES[s]}`}
        />
      );
    })}
  </div>
);

// Portal-mounted 4-step orchestrator for the parser -> backend import flow.
// Owns the state machine + reads status polling; each step is stateless w.r.t.
// the batch lifecycle.
export const ImportFlow = ({ isOpen, onClose, initialBatchId = null, readOnly = false }) => {
  const token = useSelector(selectUserToken);
  const flow = useImportFlowState();

  // Read-only entry (e.g. Import History): jump to Results with the batch loaded.
  useEffect(() => {
    if (initialBatchId != null && flow.batchId !== initialBatchId) {
      flow.setBatchId(initialBatchId);
      flow.goTo(STEPS.results);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBatchId]);

  // Polling — only when we have a batchId AND status is not terminal.
  const {
    data: batch,
    error: batchError,
  } = useGetBatchStatusQuery(
    { token, batchId: flow.batchId },
    {
      pollingInterval: 2000,
      skipPollingIfUnfocused: true,
      skip: !flow.batchId || readOnly,
    }
  );

  // Refetch once when transitioning to results — but stop the poll.
  const {
    data: finalBatch,
  } = useGetBatchStatusQuery(
    { token, batchId: flow.batchId },
    { skip: !flow.batchId }
  );

  const isTerminal =
    batch && TERMINAL_BATCH_STATUSES.has(batch.status);

  // When a running batch reaches terminal state, auto-advance to Results.
  useEffect(() => {
    if (isTerminal && flow.step === STEPS.progress) {
      flow.goTo(STEPS.results);
      const s = batch.status;
      if (s === 'completed') toast.success('Import completed');
      else if (s === 'completed_with_errors')
        toast.warning('Import completed with errors');
      else if (s === 'cancelled') toast.info('Import cancelled');
      else if (s === 'failed') toast.error('Import failed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerminal, flow.step]);

  useEffect(() => {
    if (batchError && flow.batchId) {
      // eslint-disable-next-line no-console
      console.error('[ImportFlow] status poll failed:', batchError);
    }
  }, [batchError, flow.batchId]);

  const handleClose = useCallback(() => {
    // Running import: prevent close, minimize instead + toast.
    if (
      !readOnly &&
      flow.batchId &&
      batch &&
      !TERMINAL_BATCH_STATUSES.has(batch.status)
    ) {
      flow.minimize();
      onClose();
      toast.info('Import continues in the background');
      return;
    }
    flow.reset();
    onClose();
  }, [batch, flow, onClose, readOnly]);

  // Restore from background pill.
  const handleRestore = useCallback(() => {
    flow.restore();
  }, [flow]);

  const step = flow.step;
  const activeBatch = batch || finalBatch;

  return (
    <>
      {/* When minimized we still render the pill (portal is fine) */}
      {flow.isMinimized && flow.batchId && activeBatch && (
        <BackgroundJobPill batch={activeBatch} onRestore={handleRestore} />
      )}

      <Dialog
        isOpen={isOpen && !flow.isMinimized}
        onClose={handleClose}
        classes="w-[95vw] max-w-6xl min-h-[70vh]"
        content={
          <div data-testid="parser-import-dialog" className="flex flex-col w-full gap-4">
            <div className="flex items-center justify-between w-full pr-8">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-darkGrayColor uppercase tracking-wider">
                  Parser → Backend Import
                </span>
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-blackColor"
                >
                  {STEP_TITLES[step]}
                </h2>
              </div>
              <StepDots steps={flow.steps} currentIndex={flow.stepIndex} />
            </div>

            <div className="w-full border-t border-gray-200" />

            <StepBoundary>
              {step === STEPS.scope && (
                <ScopeStep flow={flow} token={token} onCancel={handleClose} />
              )}
              {step === STEPS.preview && (
                <PreviewStep flow={flow} token={token} />
              )}
              {step === STEPS.progress && flow.batchId && (
                <ProgressStep
                  flow={flow}
                  token={token}
                  batch={activeBatch}
                  onMinimize={() => {
                    flow.minimize();
                    onClose();
                    toast.info('Import continues in the background');
                  }}
                />
              )}
              {step === STEPS.results && flow.batchId && (
                <ResultsStep
                  flow={flow}
                  token={token}
                  batch={activeBatch}
                  readOnly={readOnly}
                  onClose={handleClose}
                />
              )}
            </StepBoundary>
          </div>
        }
      />
    </>
  );
};
